import React from 'react'
import { Card, CardContent, Box, Avatar, Typography, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material'
import { Receipt as ReceiptIcon } from '@mui/icons-material'
import type { InvoiceFormData, SalesSetting } from './types'

interface BasicInfoCardProps {
  formData: InvoiceFormData
  setFormData: React.Dispatch<React.SetStateAction<InvoiceFormData>>
  branches: any[]
  staff: any[]
  salesSettings: SalesSetting[]
  disabled?: boolean
}

const BasicInfoCard: React.FC<BasicInfoCardProps> = ({ formData, setFormData, branches, staff, salesSettings, disabled }) => {
  return (
    <Card 
      elevation={2} 
      sx={{ 
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 3,
          pb: 2,
          borderBottom: '2px solid',
          borderColor: 'primary.main'
        }}>
          <Avatar sx={{ 
            bgcolor: 'primary.main', 
            width: 40, 
            height: 40,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <ReceiptIcon />
          </Avatar>
          <Box>
            <Typography 
              variant="h6" 
              color="primary" 
              fontWeight="600"
              sx={{ fontSize: '1.25rem' }}
            >
              معلومات الفاتورة
            </Typography>
            <Typography variant="body2" color="text.secondary">
              بيانات أساسية للفاتورة
            </Typography>
          </Box>
        </Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)'
          }, 
          gap: 3
        }}>
          <FormControl fullWidth size="small" disabled={!!disabled}>
            <InputLabel id="payment-type-label">نوع الفاتورة</InputLabel>
            <Select
              labelId="payment-type-label"
              value={formData.payment_type}
              label="نوع الفاتورة"
              onChange={(e) => {
                const newPaymentType = e.target.value as string;
                setFormData(prev => ({ 
                  ...prev, 
                  payment_type: newPaymentType,
                  invoice_status: '' // Reset status when payment type changes
                }));
              }}
            >
              <MenuItem value="1">نقداً</MenuItem>
              <MenuItem value="2">آجل</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small" disabled={!!disabled}>
            <InputLabel id="invoice-status-label">حالة الفاتورة</InputLabel>
            <Select
              labelId="invoice-status-label"
              value={formData.invoice_status}
              label="حالة الفاتورة"
              onChange={(e) => setFormData(prev => ({ ...prev, invoice_status: e.target.value as string }))}
            >
              {salesSettings
                .filter(setting => 
                  formData.payment_type === '1' 
                    ? setting.is_cash_visible === 1 && setting.cash_name_ar !== '-------------'
                    : setting.is_agel_visible === 1 && setting.agel_name_ar !== '-------------'
                )
                .map((setting) => (
                  <MenuItem key={setting.id} value={setting.id.toString()}>
                    {formData.payment_type === '1' ? setting.cash_name_ar : setting.agel_name_ar}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
          <TextField
            label="رقم السند"
            value={formData.document_number}
            onChange={(e) => setFormData(prev => ({ ...prev, document_number: e.target.value }))}
            fullWidth
            size="small"
            disabled={!!disabled}
          />
          <TextField
            label="تاريخ الفاتورة"
            type="date"
            value={formData.invoice_date}
            onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            disabled={!!disabled}
          />
          
          <TextField
            label="التوقيت"
            value={formData.timing}
            onChange={(e) => setFormData(prev => ({ ...prev, timing: e.target.value }))}
            fullWidth
            size="small"
            disabled={!!disabled}
          />
          <FormControl fullWidth size="small" disabled={!!disabled}>
            <InputLabel id="branch-label">الفرع</InputLabel>
            <Select
              labelId="branch-label"
              value={branches.find((b: any) => Number(b.id) === formData.def_branch) ? formData.def_branch : ''}
              label="الفرع"
              onChange={(e) => {
                const selectedId = Number(e.target.value)
                const selectedBranch = branches.find((b: any) => Number(b.id) === selectedId)
                setFormData(prev => ({
                  ...prev,
                  def_branch: selectedId,
                  branch: selectedBranch?.name_a_original || selectedBranch?.name_a || selectedBranch?.name || prev.branch
                }))
              }}
            >
              {branches.map((b: any) => (
                <MenuItem key={b.id} value={Number(b.id)}>{b.name_a_original || b.name_a || b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!!disabled}>
            <InputLabel id="staff-label">البائع</InputLabel>
            <Select
              labelId="staff-label"
              value={staff.find(u => Number(u.id) === formData.staff_user_id) ? formData.staff_user_id : ''}
              label="البائع"
              onChange={(e) => {
                const id = Number(e.target.value)
                const user = staff.find((u: any) => Number(u.id) === id)
                setFormData(prev => ({ ...prev, staff_user_id: id, seller: user?.name_ar || user?.name || prev.seller }))
              }}
            >
              {staff.map((u: any) => (
                <MenuItem key={u.id} value={Number(u.id)}>{u.name_ar || u.name || `User ${u.id}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="رقم القيد"
            value={formData.invoice_number}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default BasicInfoCard
